// FinanceVendors — vendor analysis. Supplier spend across the data's FY span
// (derived, not hardcoded) with committee, multi-year trend, and PO count.
// Expand a supplier to see its individual POs; click a PO for the full
// line-item detail. Board/staff (view_finance).
//
// NOTE: any per-vendor payment-medium metric is intentionally absent — the
// payment-medium breakdown (card vs check/ACH) will come from QuickBooks once
// connected. The PO source's payment-medium flag is provably unreliable (it
// tagged the $1.376M Seagrave pumper, the $452K ambulance, and the $343K
// remount as card-paid, which is impossible), so it is not surfaced anywhere.

import { useEffect, useMemo, useRef, useState } from "react";
import { useVendors, fetchVendorPos } from "../hooks/useFinance";
import type { VendorRow, VendorPoRow } from "../hooks/useFinance";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import ExportCsvButton from "../components/finance/ExportCsvButton";
import PageIntro from "../components/shared/PageIntro";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import type { CsvColumn } from "../lib/csv";
import { asNum, usd0, usdC } from "../components/finance/charts";
import { TH, THR, FetchErrorBanner } from "../components/finance/ui";

interface Vendor {
  supplier: string;
  committee: string;
  byFy: Record<string, number>;
  total: number;
  pos: number;
}

export default function FinanceVendors() {
  const { data, isLoading, error } = useVendors();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<VendorPoRow | null>(null);

  const { years, vendors, totalSpend } = useMemo(() => {
    const rows = (data?.rows ?? []) as VendorRow[];
    const yearsSet = new Set<string>();
    const map = new Map<string, Vendor>();
    for (const r of rows) {
      yearsSet.add(r.fiscal_year);
      const e = map.get(r.supplier) ?? {
        supplier: r.supplier,
        committee: r.committee,
        byFy: {},
        total: 0,
        pos: 0,
      };
      e.byFy[r.fiscal_year] = asNum(r.total);
      e.total += asNum(r.total);
      e.pos += asNum(r.po_count);
      if (r.committee) e.committee = r.committee;
      map.set(r.supplier, e);
    }
    const list = [...map.values()].sort((a, b) => b.total - a.total);
    const ts = list.reduce((s, v) => s + v.total, 0);
    return {
      years: [...yearsSet].sort(),
      vendors: list,
      totalSpend: ts,
    };
  }, [data]);

  // FY span of the vendor feed, derived from the data (first–last fiscal year)
  // so labels widen automatically as new years land — never pinned to FY22–26.
  const fyRangeLabel =
    years.length > 0 ? `${years[0]}–${years[years.length - 1]}` : "";

  const filtered = q.trim()
    ? vendors.filter(
        (v) =>
          v.supplier.toLowerCase().includes(q.toLowerCase()) ||
          (v.committee || "").toLowerCase().includes(q.toLowerCase()),
      )
    : vendors;

  // CSV export: flatten each (filtered) vendor into a flat record with one
  // column per fiscal year, then the rollup figures. Reflects exactly what the
  // table shows (current filter), client-side from already-loaded rows.
  const exportRows = useMemo(
    () =>
      filtered.map((v) => {
        const rec: Record<string, unknown> = {
          supplier: v.supplier,
          committee: v.committee || "",
        };
        for (const y of years) rec[y] = Math.round(v.byFy[y] ?? 0);
        rec.total = Math.round(v.total);
        rec.po_count = v.pos;
        return rec;
      }),
    [filtered, years],
  );
  const exportColumns = useMemo<CsvColumn<Record<string, unknown>>[]>(() => {
    const cols: CsvColumn<Record<string, unknown>>[] = [
      { key: "supplier", header: "Supplier" },
      { key: "committee", header: "Committee / Office" },
    ];
    for (const y of years) cols.push({ key: y, header: y });
    const totalHeader = years.length
      ? `Total Spend (${years[0]}-${years[years.length - 1]})`
      : "Total Spend";
    cols.push(
      { key: "total", header: totalHeader },
      { key: "po_count", header: "PO Count" },
    );
    return cols;
  }, [years]);

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title="Vendor Analysis"
          subtitle={`Supplier spend across ${fyRangeLabel || "all fiscal years"} — who we pay, how much, the trend, committee. Expand a supplier for its POs; click a PO for detail.`}
        />
        <PageIntro>
          Who we pay and how much, year over year — useful for spotting our
          biggest vendors and any unusual jumps in spending.
        </PageIntro>
      </div>

      <FetchErrorBanner error={error} what="vendor analysis" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPICard
          label="Total Vendor Spend"
          value={usdC(totalSpend)}
          subtitle={`${fyRangeLabel || "All years"}, all POs`}
          accent="neutral"
          isLoading={isLoading}
        />
        <KPICard
          label="Vendors"
          value={vendors.length}
          subtitle="Distinct suppliers"
          accent="neutral"
          isLoading={isLoading}
        />
        <KPICard
          label="Top Vendor"
          value={vendors[0] ? usdC(vendors[0].total) : "—"}
          subtitle={vendors[0]?.supplier.slice(0, 26) ?? ""}
          accent="neutral"
          isLoading={isLoading}
        />
      </div>

      <div className="rounded-xl border border-theme-subtle bg-theme-raised px-4 py-3 text-xs text-theme-muted leading-snug">
        Payment-medium breakdown (card vs check/ACH) will come from QuickBooks
        once connected — the payment-medium flag in the PO source is unreliable
        and is not shown.
      </div>

      <ChartCard
        title="Vendors by Spend"
        subtitle={`${filtered.length} shown · sorted by total · click ▸ to see POs`}
        empty={!isLoading && filtered.length === 0}
        minHeight={120}
        headerRight={
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter vendor / committee…"
              className="px-3 py-1.5 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none w-52"
            />
            <ExportCsvButton
              filename="dcvfd-vendor-analysis"
              rows={exportRows}
              columns={exportColumns}
            />
          </div>
        }
      >
        <div className="overflow-x-auto -mx-4 md:-mx-5 max-h-[640px] overflow-y-auto">
          <table className="w-full min-w-[880px] text-left">
            <caption className="sr-only">Vendors by spend</caption>
            <thead className="sticky top-0 z-10 bg-theme-surface">
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  Vendor
                </th>
                <th scope="col" className={TH}>
                  Committee / Office
                </th>
                {years.map((y) => (
                  <th scope="col" key={y} className={THR}>
                    {y}
                  </th>
                ))}
                <th scope="col" className={THR}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 12 }).map((_, i) => (
                    <tr
                      key={i}
                      className="hover:bg-theme-overlay/50 transition-colors"
                    >
                      {Array.from({ length: years.length + 3 }).map((__, j) => (
                        <td key={j} className="px-3 py-2">
                          <div
                            className="skeleton h-4 rounded-md"
                            style={{
                              width: `${40 + ((i * 7 + j * 11) % 50)}px`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : filtered
                    .slice(0, 200)
                    .map((v) => (
                      <VendorRowEl
                        key={v.supplier}
                        v={v}
                        years={years}
                        open={open === v.supplier}
                        onToggle={() =>
                          setOpen(open === v.supplier ? null : v.supplier)
                        }
                        onPick={setDetail}
                      />
                    ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
      <p className="text-xs text-theme-muted">
        Suppliers are the cleanest key for committee attribution (rarely
        shared). Expand a supplier to audit its individual POs; click a PO for
        the full line-item detail. Payment-medium attribution (card vs
        check/ACH) is intentionally not shown here — the payment-medium flag in
        the PO source is unreliable and will be replaced by QuickBooks once
        connected. Showing top 200 by spend.
      </p>

      {detail && <PoModal po={detail} onClose={() => setDetail(null)} />}
    </PageShell>
  );
}

function VendorRowEl({
  v,
  years,
  open,
  onToggle,
  onPick,
}: {
  v: Vendor;
  years: string[];
  open: boolean;
  onToggle: () => void;
  onPick: (p: VendorPoRow) => void;
}) {
  const [pos, setPos] = useState<VendorPoRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || pos !== null) return;
    setLoading(true);
    fetchVendorPos(v.supplier)
      .then(setPos)
      .catch(() => setPos([]))
      .finally(() => setLoading(false));
  }, [open, pos, v.supplier]);

  const colCount = years.length + 3;
  return (
    <>
      <tr
        className="hover:bg-theme-overlay/50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <td
          className="px-3 py-2 text-sm text-theme-primary max-w-[260px] truncate"
          title={v.supplier}
        >
          <span className="inline-block w-3 text-theme-muted">
            {open ? "▾" : "▸"}
          </span>{" "}
          {v.supplier}
        </td>
        <td className="px-3 py-2 text-xs text-theme-muted">
          {v.committee || "—"}
        </td>
        {years.map((y) => (
          <td
            key={y}
            className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary"
          >
            {v.byFy[y] ? (
              usdC(v.byFy[y])
            ) : (
              <span className="text-theme-muted/40">—</span>
            )}
          </td>
        ))}
        <td className="px-3 py-2 text-sm text-right tabular-nums font-semibold text-theme-primary">
          {usdC(v.total)}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={colCount} className="p-0">
            <div className="bg-theme-overlay/30 px-4 md:px-6 py-3">
              {loading ? (
                <p className="text-xs text-theme-muted py-2">
                  Loading purchase orders…
                </p>
              ) : !pos || pos.length === 0 ? (
                <p className="text-xs text-theme-muted py-2">
                  No individual PO lines on record.
                </p>
              ) : (
                <table className="w-full text-left">
                  <caption className="sr-only">
                    Purchase orders for the selected vendor
                  </caption>
                  <thead>
                    <tr className="text-theme-muted">
                      <th
                        scope="col"
                        className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider"
                      >
                        PO #
                      </th>
                      <th
                        scope="col"
                        className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider"
                      >
                        FY
                      </th>
                      <th
                        scope="col"
                        className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-right"
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pos.map((p) => (
                      <tr
                        key={p.po_number}
                        className="hover:bg-theme-overlay/50 cursor-pointer transition-colors"
                        onClick={() => onPick(p)}
                      >
                        <td className="px-2 py-1.5 text-xs font-mono text-dcvfd-accent">
                          {p.po_number}
                        </td>
                        <td
                          className="px-2 py-1.5 text-xs text-theme-secondary max-w-[360px] truncate"
                          title={p.description ?? ""}
                        >
                          {p.description || "—"}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-theme-muted">
                          {p.fiscal_year}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-right tabular-nums text-theme-primary">
                          {usd0(asNum(p.total))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PoModal({ po, onClose }: { po: VendorPoRow; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // Escape to close + a basic focus trap (Tab/Shift+Tab cycles within the
  // dialog). Focus moves to the close button on open and the keydown handler is
  // attached to the dialog so it doesn't leak to the page behind it.
  useEffect(() => {
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows: [string, string][] = [
    ["PO Number", po.po_number],
    ["Supplier", po.supplier],
    ["Amount", usd0(asNum(po.total))],
    ["Fiscal Year", po.fiscal_year ?? "—"],
    ["Order Date", po.order_date ?? "—"],
    ["Status", po.status ?? "—"],
    ["Funds Status", po.funds_status ?? "—"],
    ["Committee", po.committee ?? "—"],
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="po-modal-title"
        className="glass rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 border-b border-theme-subtle/50 flex items-start justify-between gap-4">
          <div>
            <h3
              id="po-modal-title"
              className="text-sm font-semibold text-theme-primary"
            >
              Purchase Order {po.po_number}
            </h3>
            <p className="text-xs text-theme-muted mt-0.5">{po.supplier}</p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close purchase order details"
            className="text-theme-muted hover:text-theme-primary text-lg leading-none"
          >
            ✕
          </button>
        </div>
        <div className="p-5 space-y-2">
          {po.description && (
            <p className="text-sm text-theme-secondary mb-3">
              {po.description}
            </p>
          )}
          <dl className="divide-y divide-theme-subtle/60">
            {rows.map(([k, val]) => (
              <div
                key={k}
                className="flex items-center justify-between py-2 gap-4"
              >
                <dt className="text-xs text-theme-muted">{k}</dt>
                <dd className="text-sm text-theme-primary tabular-nums text-right">
                  {val}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
